import React, { useState } from "react";
import { CloudUpload, DriveFileRenameOutline } from "@mui/icons-material";
import { Alert, Paper, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import { LayerDeleteAction, LayerUploadAction } from "fond/project/panels/InputPanel/actions";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import { MuPowerSolverLayerIds } from "fond/types";
import { ConfirmModal, LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { useSeedPhaseOverrides } from "./useSeedPhaseOverrides";

interface MuPowerPhaseOverridesPanelProps {
  versionId: string;
  hasExistingData: boolean;
  onDelete: () => void | Promise<void>;
}

/**
 * Screen for the phase overrides layer.
 */
const MuPowerPhaseOverridesPanel: React.FC<MuPowerPhaseOverridesPanelProps> = ({ versionId, hasExistingData, onDelete }) => {
  const { open } = useStackedNavigationContext();
  const { hasDecisionPoints, isSeeding, isLoading, seedPhaseOverrides } = useSeedPhaseOverrides(versionId);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const handleEditClick = () => {
    open(`${MuPowerSolverLayerIds.phaseOverrides}-draw`);
  };

  const handleImportClick = async () => {
    setShowReplaceConfirm(false);
    try {
      await seedPhaseOverrides();
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Failed to import phases.", { variant: "error" });
    }
  };

  return (
    <LayerPanel title="Phase overrides" layerKey={MuPowerSolverLayerIds.phaseOverrides}>
      <ConfirmModal
        open={showReplaceConfirm}
        header="Replace existing phases?"
        confirmText="Continue"
        cancelText="Cancel"
        color="error"
        content={
          <Typography variant="body2">This will replace all existing phase data with the solve output. This action cannot be undone.</Typography>
        }
        onConfirm={handleImportClick}
        onCancel={() => setShowReplaceConfirm(false)}
      />
      {isSeeding ? (
        <>
          <Alert severity="info" sx={{ my: 1 }}>
            FOND is now processing your data, and it will be visible on the map shortly.
          </Alert>
          <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
            <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing phases" subtext="Processing..." />
          </Paper>
        </>
      ) : (
        <>
          <StackedNavigationButton
            data-testid="edit-phase-overrides-button"
            icon={<DriveFileRenameOutline />}
            title="Edit"
            subtitle="Edit phase values on the map"
            onClick={handleEditClick}
            disabled={!hasExistingData}
          />
          <StackedNavigationButton
            data-testid="import-phases-button"
            icon={<CloudUpload />}
            title={hasExistingData ? "Replace phases" : "Import solve phases"}
            subtitle={hasExistingData ? "Reset phases to solve output" : "Import phases from the current design"}
            onClick={hasExistingData ? () => setShowReplaceConfirm(true) : handleImportClick}
            disabled={isLoading || !hasDecisionPoints}
          />
          <LayerUploadAction />
          <LayerDeleteAction disabled={!hasExistingData} onDelete={onDelete} />
        </>
      )}
    </LayerPanel>
  );
};

export default MuPowerPhaseOverridesPanel;
